import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import {
  ACTION,
  Button,
  ButtonVariants,
  CustomerExecutionStrategyEnum,
  FormControlSizes,
  HamburgerMenu,
  IconName,
  MixpanelEvent,
  NotificationVariants,
  ORD_TEXT_TO_STATUS_MAPPING,
  OrderStatusText,
  Post,
  abbreviateId,
  canTraderAdminForceCancelCustomerOrder,
  filterByCellValueMenuItem,
  includesEnumValue,
  isOrderComplete,
  request,
  useCallbackRef,
  useDisclosureWithContext,
  useGlobalDialog,
  useGlobalToasts,
  useJsonModal,
  useMixpanel,
  useUserContext,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type CustomerOrder,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useFeatureFlag, useRoleAuth } from '../../../hooks';
import { useNavigateToOrderDetails } from '../../../hooks/useNavigateToOrderDetails';
import { CancelOrderDialog, ForceFillOrderDialog, ModifyOrderSpreadDialog } from './Dialogs';
import { CancelS2FCustomerOrderDialog } from './Dialogs/CancelS2FCustomerOrderDialog';
import { useCopyCustomerOrderTextMenuItem } from './useCopyCustomerOrderTextMenuItem';
import { colIDToFilterBuilderKey } from './useCustomerOrdersFilter';

export function CustomerOrdersMenu({
  data: customerOrder,
  onModifySpread,
  onForceCancelTalos,
  onCancel,
  onForceFill,
  onForceCancel,
  ...params
}: ICellRendererParams & {
  onShowJson(data: CustomerOrder): void;
  onCancel(data: CustomerOrder): void;
  onForceCancel(data: CustomerOrder): void;
  onForceCancelTalos(data: CustomerOrder): void;
  onModifySpread(data: CustomerOrder): void;
  onForceFill(data: CustomerOrder): void;
}) {
  const { isAuthorized } = useRoleAuth();
  const { allowCancellingS2FCustomerOrder } = useFeatureFlag();

  const showForceCancelS2FOption =
    allowCancellingS2FCustomerOrder && isAuthorized(ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER);

  return (
    <>
      <Button
        size={FormControlSizes.Small}
        width="100%"
        ghost={true}
        onClick={() => onModifySpread(customerOrder)}
        disabled={isOrderComplete(customerOrder.OrdStatus)}
      >
        Modify spread
      </Button>
      <Button
        size={FormControlSizes.Small}
        width="100%"
        ghost={true}
        onClick={() => onCancel(customerOrder)}
        disabled={isOrderComplete(customerOrder.OrdStatus) || !isAuthorized(ACTION.CANCEL_ORDER)}
      >
        Cancel
      </Button>
      {isAuthorized(ACTION.FORCE_FILL_ORDER) && (
        <Button size={FormControlSizes.Small} width="100%" ghost={true} onClick={() => onForceFill(customerOrder)}>
          Force fill
        </Button>
      )}
      {showForceCancelS2FOption && (
        <Button
          size={FormControlSizes.Small}
          width="100%"
          ghost={true}
          onClick={() => onForceCancel(customerOrder)}
          disabled={!customerOrder.canBeForceCancelledByTraderAdmin}
        >
          Force cancel
        </Button>
      )}
      {isAuthorized(ACTION.FORCE_CANCEL_ORDER) && (
        <Button
          size={FormControlSizes.Small}
          width="100%"
          ghost={true}
          onClick={() => onForceCancelTalos(customerOrder)}
          variant={ButtonVariants.Negative}
        >
          Force cancel (Talos only)
        </Button>
      )}
      <HamburgerMenu {...params} data={customerOrder} />
    </>
  );
}

export function useCustomerOrdersMenu({
  openClause,
  filterableProperties,
  generateOrderDetailsRoute,
  layoutId,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
  layoutId: string | undefined;
}): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const { orgApiEndpoint } = useUserContext();
  const { open: openGlobalDialog } = useGlobalDialog();
  const { add: addToast } = useGlobalToasts();
  const { isAuthorized } = useRoleAuth();
  const { allowCancellingS2FCustomerOrder } = useFeatureFlag();
  const showForceFill = useCallbackRef((data: CustomerOrder): boolean => {
    return (
      !isOrderComplete(data.OrdStatus) &&
      isAuthorized(ACTION.DEALER_TRADING) &&
      isAuthorized(ACTION.FORCE_FILL_ORDER) &&
      includesEnumValue(ORD_TEXT_TO_STATUS_MAPPING[OrderStatusText.Open], data.OrdStatus)
    );
  });

  const copyCustomerOrderExecutionTextMenuItem = useCopyCustomerOrderTextMenuItem();

  const forceFillOrderDialog = useDisclosureWithContext<string>();
  const modifyCustomerOrderSpreadDialog = useDisclosureWithContext<string>();
  const cancelCustomerOrderDialog = useDisclosureWithContext<string>();
  const forceCancelCustomerOrderDialog = useDisclosureWithContext<string>();

  const getOrderContextMenuItem = useCallback(
    (order: CustomerOrder) => {
      return {
        name: 'Order Context',
        action: () => {
          mixpanel.track(MixpanelEvent.OpenCustomerOrderContext);
          window.open(`${orgApiEndpoint}/customer/orders/${order.OrderID}/context`, '_blank');
        },
        icon: `<i class="ag-icon ${IconName.ExternalLink}"/>`,
      };
    },
    [mixpanel, orgApiEndpoint]
  );

  const getCustomerOrderPnLSummaryMenuItem = useCallback(
    (customerOrder: CustomerOrder) => {
      if (!isAuthorized(ACTION.VIEW_MONITORING_ORDER_CONTEXT)) {
        return null;
      }
      return {
        name: 'Regenerate PnL Summary (Talos only)',
        action: () => {
          mixpanel.track(MixpanelEvent.GenerateCustomerOrderSummary);
          Post(orgApiEndpoint, `/customer/orders/${customerOrder.OrderID}/summary-v2/generate`)
            .then(() => {
              addToast({
                text: `PnL Summary regenerated.`,
                variant: NotificationVariants.Positive,
              });
            })
            .catch((e: ErrorEvent) => {
              addToast({
                text: `Could not regenerate PnL Summary: ${e.message}`,
                variant: NotificationVariants.Negative,
              });
            });
        },
        icon: `<i class="ag-icon ${IconName.Refresh}"/>`,
      };
    },
    [addToast, isAuthorized, mixpanel, orgApiEndpoint]
  );

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CustomerOrder>) => {
    const data = params?.node?.data;
    const groupData = params?.node?.groupData;
    if (isNil(data) && isNil(groupData)) {
      return [];
    }

    // We allow Trader Admins (org users) to Force Cancel very specific S2F orders. This is different from Cancel, and Force Cancel (Talos).
    const showForceCancelS2FOption =
      allowCancellingS2FCustomerOrder && isAuthorized(ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER);

    return compact([
      ...filterByCellValueMenuItem({ params, filterableProperties, openClause, colIDToFilterBuilderKey, mixpanel }),
      ...(data
        ? [
            getOrderContextMenuItem(data),
            getCustomerOrderPnLSummaryMenuItem(data),
            data.ExecutionStrategy !== CustomerExecutionStrategyEnum.OrderAcceptance && {
              name: 'Open details',
              action: () => handleOpenHedgeOrderDetails(data),
              icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
            },
            copyCustomerOrderExecutionTextMenuItem(data),
            {
              name: `Modify spread`,
              action: () => handleModifySpread(data),
              disabled: isOrderComplete(data.OrdStatus),
            },
            {
              name: `Cancel`,
              action: () => handleCancel(data),
              disabled: isOrderComplete(data.OrdStatus),
            },
            showForceCancelS2FOption && {
              name: 'Force cancel',
              action: () => handleForceCancel(data),
              disabled: !canTraderAdminForceCancelCustomerOrder(data),
            },
            showForceFill(data)
              ? {
                  name: `Force fill`,
                  action: () => handleForceFill(data),
                }
              : null,
            isAuthorized(ACTION.FORCE_CANCEL_ORDER)
              ? {
                  name: `Force cancel (Talos only)`,
                  action: () => handleForceCancelTalos(data),
                }
              : null,
          ]
        : []),
    ]);
  });

  const onOpenDetails = useNavigateToOrderDetails(generateOrderDetailsRoute, layoutId);

  const handleOpenHedgeOrderDetails = useCallbackRef((data: CustomerOrder) => {
    onOpenDetails(data.OrderID, 'customer-order', 'details');
  });

  const handleCancel = useCallbackRef((data: CustomerOrder) => {
    cancelCustomerOrderDialog.open(data.OrderID);
  });

  const handleForceCancel = useCallbackRef((data: CustomerOrder) => {
    forceCancelCustomerOrderDialog.open(data.OrderID);
  });

  const handleModifySpread = useCallbackRef((data: CustomerOrder) => {
    modifyCustomerOrderSpreadDialog.open(data.OrderID);
  });

  // Force fill
  const handleForceFill = useCallbackRef((data: CustomerOrder) => {
    forceFillOrderDialog.open(data.OrderID);
  });

  // Force cancel (Talos)
  const handleForceCancelTalos = useCallbackRef((data: CustomerOrder) => {
    openGlobalDialog({
      title: 'Force cancel customer order',
      content: <>{`Are you sure you want to force cancel customer order #${abbreviateId(data.OrderID)}?`}</>,
      onConfirm: () => {
        request('PUT', `${orgApiEndpoint}/admin/customer/orders/${data.OrderID}/force-cancel`)
          .then(() => {
            addToast({
              text: `Order force cancelled.`,
              variant: NotificationVariants.Positive,
            });
          })
          .catch((e: ErrorEvent) =>
            addToast({
              text: `Could not force cancel customer order: ${e.message}`,
              variant: NotificationVariants.Negative,
            })
          );
      },
    });
  });

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        <ModifyOrderSpreadDialog {...modifyCustomerOrderSpreadDialog} />
        <CancelOrderDialog {...cancelCustomerOrderDialog} />
        <ForceFillOrderDialog {...forceFillOrderDialog} />
        <CancelS2FCustomerOrderDialog {...forceCancelCustomerOrderDialog} />
      </>
    ),
    [
      jsonModal,
      modifyCustomerOrderSpreadDialog,
      cancelCustomerOrderDialog,
      forceFillOrderDialog,
      forceCancelCustomerOrderDialog,
    ]
  );

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => (
              <CustomerOrdersMenu
                {...params}
                onShowJson={handleClickJson}
                onModifySpread={handleModifySpread}
                onCancel={handleCancel}
                onForceFill={handleForceFill}
                onForceCancel={handleForceCancel}
                onForceCancelTalos={handleForceCancelTalos}
              />
            ),
          },
        },
      ] satisfies ColumnDef<CustomerOrder>[]),
    [handleCancel, handleForceFill, handleModifySpread, handleClickJson, handleForceCancel, handleForceCancelTalos]
  );

  return {
    columns,
    getCustomContextMenuItems,
    dialogs,
  };
}
